import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useEditorRef } from 'platejs/react'
import type { PlateElementProps } from 'platejs/react'

import { InlineCombobox } from '@buffer-mono/popcorn/rich-text'

import { searchEmojis } from '~publish/helpers/emojiSearch'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import { EmojiElement } from '../../nodes'
import type { EmojiSearchElement } from '../../nodes'
import { createEmojiOnSelectItem } from '../../transforms/createEmojiOnSelectItem'
import type { EmojiPluginOptions, EmojiSearchData } from '../../types'
import { EmojiSuggestion } from '../EmojiSuggestion/EmojiSuggestion'

import styles from './EmojiSearchElementUI.module.css'

const isEmojiPlugin = (
  plugin: unknown,
): plugin is { options: EmojiPluginOptions } =>
  typeof plugin === 'object' &&
  plugin !== null &&
  'options' in plugin &&
  typeof plugin.options === 'object' &&
  plugin.options !== null

export const EmojiSearchElementUI = ({
  attributes,
  children,
  element,
}: PlateElementProps<EmojiSearchElement>): JSX.Element => {
  const editor = useEditorRef() as BufferEditor
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<EmojiSearchData[]>([])
  // InlineCombobox.Content anchors to the input by default, which sits to
  // the right of the (hidden) trigger-character span inside Root — visibly
  // offset from where the user actually typed ":". Root's own node starts
  // at the trigger, so anchor there instead.
  const rootRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    // Clear stale results eagerly: otherwise a later failed/empty search
    // would leave the previous query's items selectable, and an empty
    // result never gets a chance to render since the popover only opens
    // once there's a status message or item to show.
    setItems([])

    if (!search) return

    const plugin = editor.meta.pluginList.find(
      (candidate) => candidate.key === EmojiElement.type,
    )
    const maxSuggestions = isEmojiPlugin(plugin)
      ? plugin.options.maxSuggestions
      : undefined

    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const results = await searchEmojis(search)
        if (cancelled) return
        setItems(results.slice(0, maxSuggestions))
      } catch (error) {
        if (!cancelled) setItems([])
        // eslint-disable-next-line no-console
        console.warn('Failed to search emojis:', error)
      }
    })()

    return (): void => {
      cancelled = true
    }
  }, [search, editor])

  const handleSelect = (emoji: EmojiSearchData): void => {
    createEmojiOnSelectItem(EmojiElement.type)(editor, emoji)
  }

  return (
    <span {...attributes}>
      <InlineCombobox.Root
        ref={rootRef}
        trigger={element.trigger}
        value={search}
        onValueChange={setSearch}
      >
        <InlineCombobox.Input aria-label="Search emoji" />
        <InlineCombobox.Content anchorRef={rootRef} className={styles.content}>
          {search.length === 0 && (
            <InlineCombobox.Status>Type to search emoji</InlineCombobox.Status>
          )}
          {search.length > 0 && items.length === 0 && (
            <InlineCombobox.Status>No emoji matched</InlineCombobox.Status>
          )}
          <InlineCombobox.List
            aria-label="Emoji results"
            className={styles.list}
          >
            {items.map((emoji) => (
              <InlineCombobox.Item
                key={emoji.id}
                value={emoji.id}
                textValue={emoji.name}
                onSelect={() => handleSelect(emoji)}
                className={styles.item}
              >
                <EmojiSuggestion emoji={emoji} />
              </InlineCombobox.Item>
            ))}
          </InlineCombobox.List>
        </InlineCombobox.Content>
      </InlineCombobox.Root>
      {children}
    </span>
  )
}
